import { useMemo, useState } from "react";

import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { cn } from "@pontimed/ui";
import { AnimatePresence, m } from "framer-motion";
import { Activity, AlertCircle, ChevronDown, ChevronUp } from "lucide-react";

// TODO: dedupe with journal-tab JournalEntryData
interface JournalEntryData {
  id: string;
  type: string;
  title: string;
  notes: string | null;
  eventDate: string;
  tags: string[];
  status: string;
  isPinned: boolean;
  metadata: Record<string, unknown> | null;
  providerId: string | null;
  createdAt: string;
  _count: { attachments: number; reminders: number };
  reminders: { id: string; remindAt: string; message: string; status: string }[];
}

/* ─── Reference Ranges ─── */

const METRIC_TYPES = [
  "blood_pressure",
  "glucose",
  "weight",
  "temperature",
  "heart_rate",
  "oxygen_saturation",
] as const;

type MetricType = (typeof METRIC_TYPES)[number];

interface RefRange {
  min?: number;
  max?: number;
  unit: string;
}

const REFERENCE_RANGES: Record<
  MetricType,
  RefRange & { systolic?: RefRange; diastolic?: RefRange }
> = {
  blood_pressure: {
    unit: "mmHg",
    systolic: { min: 90, max: 120, unit: "mmHg" },
    diastolic: { min: 60, max: 80, unit: "mmHg" },
  },
  glucose: { min: 70, max: 100, unit: "mg/dL" },
  weight: { unit: "kg" },
  temperature: { min: 36.1, max: 37.2, unit: "°C" },
  heart_rate: { min: 60, max: 100, unit: "bpm" },
  oxygen_saturation: { min: 95, max: 100, unit: "%" },
};

/* ─── Types ─── */

interface MetricPoint {
  date: string;
  value: number;
  systolic?: number;
  diastolic?: number;
}

interface MetricSeries {
  type: MetricType;
  points: MetricPoint[];
}

/* ─── Extract data ─── */

function extractMetrics(entries: JournalEntryData[]): MetricSeries[] {
  const map = new Map<MetricType, MetricPoint[]>();

  for (const entry of entries) {
    if (entry.type !== "metric" || !entry.metadata) continue;
    const meta = entry.metadata as Record<string, unknown>;
    const metricType = meta.metricType as MetricType | undefined;
    if (!metricType || !METRIC_TYPES.includes(metricType)) continue;

    const point: MetricPoint = { date: entry.eventDate, value: 0 };

    if (metricType === "blood_pressure") {
      const sys = meta.systolic as number | undefined;
      const dia = meta.diastolic as number | undefined;
      if (typeof sys !== "number" || typeof dia !== "number") continue;
      point.systolic = sys;
      point.diastolic = dia;
      point.value = sys; // primary for sorting
    } else {
      const val = meta.value as number | undefined;
      if (typeof val !== "number") continue;
      point.value = val;
    }

    if (!map.has(metricType)) map.set(metricType, []);
    map.get(metricType)!.push(point);
  }

  const result: MetricSeries[] = [];
  for (const [type, points] of map) {
    points.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    if (points.length >= 1) result.push({ type, points });
  }
  return result;
}

/* ─── SVG Chart ─── */

const CHART_W = 400;
const CHART_H = 200;
const PAD = { top: 20, right: 20, bottom: 40, left: 50 };
const PLOT_W = CHART_W - PAD.left - PAD.right;
const PLOT_H = CHART_H - PAD.top - PAD.bottom;

function MetricLineChart({
  series,
  hoveredIdx,
  setHoveredIdx,
}: {
  series: MetricSeries;
  hoveredIdx: number | null;
  setHoveredIdx: (i: number | null) => void;
}) {
  const { type, points } = series;
  const ref = REFERENCE_RANGES[type];
  const isBp = type === "blood_pressure";

  // Compute Y range
  const allVals: number[] = [];
  for (const p of points) {
    if (isBp) {
      allVals.push(p.systolic!, p.diastolic!);
    } else {
      allVals.push(p.value);
    }
  }
  if (ref.min !== undefined) allVals.push(ref.min);
  if (ref.max !== undefined) allVals.push(ref.max);
  if (isBp && ref.systolic && ref.diastolic) {
    allVals.push(ref.systolic.min!, ref.systolic.max!, ref.diastolic.min!, ref.diastolic.max!);
  }

  let yMin = Math.min(...allVals);
  let yMax = Math.max(...allVals);
  const yPad = (yMax - yMin) * 0.15 || 1;
  yMin -= yPad;
  yMax += yPad;

  const xScale = (i: number) =>
    PAD.left + (points.length === 1 ? PLOT_W / 2 : (i / (points.length - 1)) * PLOT_W);
  const yScale = (v: number) => PAD.top + PLOT_H - ((v - yMin) / (yMax - yMin)) * PLOT_H;

  const yTicks = Array.from({ length: 5 }, (_, i) => yMin + ((yMax - yMin) * i) / 4);

  const makeLine = (accessor: (p: MetricPoint) => number) =>
    points.map((p, i) => `${xScale(i)},${yScale(accessor(p))}`).join(" ");

  const isOutsideRange = (val: number, min?: number, max?: number) =>
    (min !== undefined && val < min) || (max !== undefined && val > max);

  return (
    <svg
      viewBox={`0 0 ${CHART_W} ${CHART_H}`}
      className="w-full"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* Reference range bands */}
      {isBp && ref.systolic && ref.diastolic ? (
        <>
          <rect
            x={PAD.left}
            y={yScale(ref.systolic.max!)}
            width={PLOT_W}
            height={yScale(ref.systolic.min!) - yScale(ref.systolic.max!)}
            className="fill-primary/8"
            rx={2}
          />
          <rect
            x={PAD.left}
            y={yScale(ref.diastolic.max!)}
            width={PLOT_W}
            height={yScale(ref.diastolic.min!) - yScale(ref.diastolic.max!)}
            className="fill-indigo-500/8"
            rx={2}
          />
        </>
      ) : ref.min !== undefined && ref.max !== undefined ? (
        <rect
          x={PAD.left}
          y={yScale(ref.max)}
          width={PLOT_W}
          height={yScale(ref.min) - yScale(ref.max)}
          className="fill-primary/10"
          rx={2}
        />
      ) : null}

      {/* Grid */}
      {yTicks.map((tick, i) => (
        <g key={i}>
          <line
            x1={PAD.left}
            y1={yScale(tick)}
            x2={PAD.left + PLOT_W}
            y2={yScale(tick)}
            className="stroke-border"
            strokeWidth={0.5}
            strokeDasharray="4,4"
          />
          <text
            x={PAD.left - 6}
            y={yScale(tick) + 3}
            textAnchor="end"
            className="fill-muted-foreground"
            fontSize={9}
          >
            {tick.toFixed(tick % 1 === 0 ? 0 : 1)}
          </text>
        </g>
      ))}

      {/* X labels */}
      {points.map((p, i) => {
        const d = new Date(p.date);
        return (
          <text
            key={i}
            x={xScale(i)}
            y={CHART_H - 5}
            textAnchor="middle"
            className="fill-muted-foreground"
            fontSize={9}
          >
            {`${d.getDate()}/${d.getMonth() + 1}`}
          </text>
        );
      })}

      {/* Unit */}
      <text x={4} y={PAD.top - 6} className="fill-muted-foreground" fontSize={8}>
        {ref.unit}
      </text>

      {/* Lines */}
      {points.length > 1 && (
        <>
          {isBp ? (
            <>
              <polyline
                points={makeLine((p) => p.systolic!)}
                fill="none"
                className="stroke-primary"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <polyline
                points={makeLine((p) => p.diastolic!)}
                fill="none"
                className="stroke-indigo-500"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </>
          ) : (
            <polyline
              points={makeLine((p) => p.value)}
              fill="none"
              className="stroke-primary"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </>
      )}

      {/* Data points */}
      {points.map((p, i) => {
        const isHovered = hoveredIdx === i;

        if (isBp) {
          const sysOut = isOutsideRange(p.systolic!, ref.systolic?.min, ref.systolic?.max);
          const diaOut = isOutsideRange(p.diastolic!, ref.diastolic?.min, ref.diastolic?.max);
          return (
            <g key={i}>
              <circle
                cx={xScale(i)}
                cy={yScale((p.systolic! + p.diastolic!) / 2)}
                r={16}
                fill="transparent"
                className="cursor-pointer"
                onPointerEnter={() => setHoveredIdx(i)}
                onPointerLeave={() => setHoveredIdx(null)}
              />
              <circle
                cx={xScale(i)}
                cy={yScale(p.systolic!)}
                r={isHovered ? 5 : 3.5}
                className={cn("transition-all", sysOut ? "fill-red-500" : "fill-primary")}
              />
              <circle
                cx={xScale(i)}
                cy={yScale(p.diastolic!)}
                r={isHovered ? 5 : 3.5}
                className={cn("transition-all", diaOut ? "fill-red-500" : "fill-indigo-500")}
              />
              {isHovered && (
                <g>
                  <rect
                    x={xScale(i) - 42}
                    y={yScale(p.systolic!) - 28}
                    width={84}
                    height={20}
                    rx={4}
                    className="fill-foreground/90"
                  />
                  <text
                    x={xScale(i)}
                    y={yScale(p.systolic!) - 15}
                    textAnchor="middle"
                    className="fill-background"
                    fontSize={10}
                    fontWeight={600}
                  >
                    {p.systolic}/{p.diastolic} {ref.unit}
                  </text>
                </g>
              )}
            </g>
          );
        }

        const out = isOutsideRange(p.value, ref.min, ref.max);
        return (
          <g key={i}>
            <circle
              cx={xScale(i)}
              cy={yScale(p.value)}
              r={16}
              fill="transparent"
              className="cursor-pointer"
              onPointerEnter={() => setHoveredIdx(i)}
              onPointerLeave={() => setHoveredIdx(null)}
            />
            <circle
              cx={xScale(i)}
              cy={yScale(p.value)}
              r={isHovered ? 5 : 3.5}
              className={cn("transition-all", out ? "fill-red-500" : "fill-primary")}
            />
            {isHovered && (
              <g>
                <rect
                  x={xScale(i) - 36}
                  y={yScale(p.value) - 28}
                  width={72}
                  height={20}
                  rx={4}
                  className="fill-foreground/90"
                />
                <text
                  x={xScale(i)}
                  y={yScale(p.value) - 15}
                  textAnchor="middle"
                  className="fill-background"
                  fontSize={10}
                  fontWeight={600}
                >
                  {p.value} {ref.unit}
                </text>
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
}

/* ─── Main Component ─── */

export function HealthMetricsChart({ entries }: { entries: JournalEntryData[] }) {
  const { t } = useLingui();
  const [expanded, setExpanded] = useState(false);
  const [selectedType, setSelectedType] = useState<MetricType | null>(null);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

  const allSeries = useMemo(() => extractMetrics(entries), [entries]);

  const activeType = selectedType ?? allSeries[0]?.type ?? null;
  const activeSeries = allSeries.find((s) => s.type === activeType) ?? null;

  if (allSeries.length === 0) return null;

  const hasEnoughData = activeSeries && activeSeries.points.length >= 2;

  const metricLabel = (type: MetricType): string => {
    switch (type) {
      case "blood_pressure":
        return t`Blood Pressure`;
      case "glucose":
        return t`Glucose`;
      case "weight":
        return t`Weight`;
      case "temperature":
        return t`Temperature`;
      case "heart_rate":
        return t`Heart Rate`;
      case "oxygen_saturation":
        return t`SpO2`;
    }
  };

  return (
    <div className="border-border bg-card rounded-xl border">
      {/* Header */}
      <button
        onClick={() => setExpanded((e) => !e)}
        className="flex min-h-[48px] w-full items-center gap-3 p-4"
      >
        <div className="flex size-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400">
          <Activity className="size-4" />
        </div>
        <div className="flex-1 text-left">
          <p className="text-foreground text-sm font-semibold">
            <Trans>Health Metrics</Trans>
          </p>
          <p className="text-muted-foreground text-xs">
            <Plural value={allSeries.length} one="# metric tracked" other="# metrics tracked" />
          </p>
        </div>
        {expanded ? (
          <ChevronUp className="text-muted-foreground size-4" />
        ) : (
          <ChevronDown className="text-muted-foreground size-4" />
        )}
      </button>

      {/* Content */}
      <AnimatePresence>
        {expanded && (
          <m.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="border-border space-y-3 border-t px-4 pt-3 pb-4">
              {/* Metric type selector */}
              <div className="relative">
                <select
                  value={activeType || ""}
                  onChange={(e) => {
                    setSelectedType(e.target.value as MetricType);
                    setHoveredIdx(null);
                  }}
                  className="border-border bg-background text-foreground focus:ring-primary/30 focus:border-primary min-h-[44px] w-full cursor-pointer appearance-none rounded-lg border px-3 pr-8 text-sm outline-none focus:ring-2 focus:ring-inset"
                >
                  {allSeries.map((s) => (
                    <option key={s.type} value={s.type}>
                      {metricLabel(s.type)} ({REFERENCE_RANGES[s.type].unit})
                    </option>
                  ))}
                </select>
                <ChevronDown className="text-muted-foreground pointer-events-none absolute top-1/2 right-2 size-4 -translate-y-1/2" />
              </div>

              {/* Chart */}
              {hasEnoughData ? (
                <div className="bg-muted/30 rounded-lg p-2">
                  <MetricLineChart
                    series={activeSeries}
                    hoveredIdx={hoveredIdx}
                    setHoveredIdx={setHoveredIdx}
                  />
                </div>
              ) : (
                <div className="flex flex-col items-center py-6">
                  <AlertCircle className="text-muted-foreground size-8" />
                  <p className="text-muted-foreground mt-2 text-sm">
                    <Trans>Not enough data to show a chart</Trans>
                  </p>
                </div>
              )}

              {/* Legend for BP */}
              {activeType === "blood_pressure" && (
                <div className="text-muted-foreground flex flex-wrap gap-4 text-xs">
                  <span className="flex items-center gap-1.5">
                    <span className="bg-primary inline-block size-2.5 rounded-full" />
                    <Trans>Systolic</Trans> (90-120)
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block size-2.5 rounded-full bg-indigo-500" />
                    <Trans>Diastolic</Trans> (60-80)
                  </span>
                </div>
              )}

              {/* Reference range legend for non-BP */}
              {activeType !== "blood_pressure" && activeType !== "weight" && activeSeries && (
                <div className="text-muted-foreground flex items-center gap-2 text-xs">
                  <span className="bg-primary/10 border-primary/20 inline-block size-3 rounded border" />
                  <Trans>
                    Reference range: {REFERENCE_RANGES[activeType].min!}–
                    {REFERENCE_RANGES[activeType].max!} {REFERENCE_RANGES[activeType].unit}
                  </Trans>
                </div>
              )}
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}
